import type {
  FrequencyResponse,
  PersonalRecordsResponse,
  StatsWindow,
  VolumeResponse,
  Workout,
} from '@onflow/shared';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { api } from '../lib/api.js';

interface WorkoutPage {
  items: Workout[];
  nextCursor: string | null;
}

export function useVolume(window: StatsWindow = '12w') {
  return useQuery({
    queryKey: ['stats', 'volume', window],
    queryFn: () => api<VolumeResponse>(`/stats/volume?window=${window}`),
  });
}

export function useFrequency(window: StatsWindow = '12w') {
  return useQuery({
    queryKey: ['stats', 'frequency', window],
    queryFn: () => api<FrequencyResponse>(`/stats/frequency?window=${window}`),
  });
}

export function usePersonalRecords() {
  return useQuery({
    queryKey: ['stats', 'prs'],
    queryFn: () => api<PersonalRecordsResponse>('/stats/personal-records'),
  });
}

export function useRecentWorkouts(limit = 5) {
  return useQuery({
    queryKey: ['workouts', 'recent', limit],
    queryFn: () => api<WorkoutPage>(`/workouts?limit=${limit}`),
  });
}

export function useWorkoutHistory(limit = 30) {
  return useInfiniteQuery({
    queryKey: ['workouts', 'history', limit],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      api<WorkoutPage>(
        `/workouts?limit=${limit}${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
}
